import type { LayoutBlock } from '@web-studio/shared-schemas';
import {
  PACKAGE_TIER_KEYS,
  PLATFORM_VERTICAL_KEYS,
  THEME_ID,
  type PackageTierKey,
  type PlatformVerticalKey,
} from '@web-studio/shared-schemas';
import { restaurantMinimumPage } from './pages/restaurant-minimum';
import { restaurantPlusPage } from './pages/restaurant-plus';
import { restaurantRecommendedPage } from './pages/restaurant-recommended';
import { sushiBarMinimumPage } from './pages/sushi-bar-minimum';
import { sushiBarPlusPage } from './pages/sushi-bar-plus';
import { sushiBarRecommendedPage } from './pages/sushi-bar-recommended';
import { cafeRecommendedPage } from './pages/cafe-recommended';
import { barRecommendedPage } from './pages/bar-recommended';
import { foodTruckRecommendedPage } from './pages/food-truck-recommended';

export interface PageFixtureDefinition {
  id: string;
  label: string;
  vertical: PlatformVerticalKey;
  tier: PackageTierKey;
  blocks: LayoutBlock[];
  defaultThemeId: string;
}

export const PAGE_FIXTURES: PageFixtureDefinition[] = [
  {
    id: 'restaurant-minimum',
    label: 'Restaurant · Essential Presence',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.minimum,
    blocks: restaurantMinimumPage,
    defaultThemeId: THEME_ID.michelinDark,
  },
  {
    id: 'restaurant-recommended',
    label: 'Restaurant · Growth Engine',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.recommended,
    blocks: restaurantRecommendedPage,
    defaultThemeId: THEME_ID.michelinDark,
  },
  {
    id: 'restaurant-plus',
    label: 'Restaurant · Premium Experience',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.plus,
    blocks: restaurantPlusPage,
    defaultThemeId: THEME_ID.michelinDark,
  },
  {
    id: 'sushi-bar-minimum',
    label: 'Sushi Bar · Essential Presence',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.minimum,
    blocks: sushiBarMinimumPage,
    defaultThemeId: THEME_ID.hinokiCounter,
  },
  {
    id: 'sushi-bar-recommended',
    label: 'Sushi Bar · Growth Engine',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.recommended,
    blocks: sushiBarRecommendedPage,
    defaultThemeId: THEME_ID.hinokiCounter,
  },
  {
    id: 'sushi-bar-plus',
    label: 'Sushi Bar · Premium Experience',
    vertical: PLATFORM_VERTICAL_KEYS.restaurant,
    tier: PACKAGE_TIER_KEYS.plus,
    blocks: sushiBarPlusPage,
    defaultThemeId: THEME_ID.hinokiCounter,
  },
  {
    id: 'cafe-recommended',
    label: 'Cafe · Active Cafe',
    vertical: PLATFORM_VERTICAL_KEYS.cafe,
    tier: PACKAGE_TIER_KEYS.recommended,
    blocks: cafeRecommendedPage,
    defaultThemeId: THEME_ID.brutalistGrind,
  },
  {
    id: 'bar-recommended',
    label: 'Bar · Lounge Standard',
    vertical: PLATFORM_VERTICAL_KEYS.bar,
    tier: PACKAGE_TIER_KEYS.recommended,
    blocks: barRecommendedPage,
    defaultThemeId: THEME_ID.neonNoir,
  },
  {
    id: 'food-truck-recommended',
    label: 'Food Truck · Rolling Kitchen',
    vertical: PLATFORM_VERTICAL_KEYS.foodTruck,
    tier: PACKAGE_TIER_KEYS.recommended,
    blocks: foodTruckRecommendedPage,
    defaultThemeId: THEME_ID.boldStreet,
  },
];

export function getPageFixture(pageId: string): PageFixtureDefinition | undefined {
  return PAGE_FIXTURES.find((p) => p.id === pageId);
}

/** Page fixtures for a vertical × package tier (may be multiple layout variants). */
export function findPageFixtures(
  vertical: PlatformVerticalKey,
  tier: PackageTierKey,
): PageFixtureDefinition[] {
  return PAGE_FIXTURES.filter((p) => p.vertical === vertical && p.tier === tier);
}
